<template>
  <div id="signup">
    <PasswordReset v-if="showPasswordReset" @close="togglePasswordReset()"></PasswordReset>
    <SiteHeader/>
    <section>
      <div class="col1">
        <div class="package" v-if="packageData && !simpleSignup">
          <!-- Package name & description -->
          <h2 v-html="`Selected Package: ${packageData.name}`"/>
          <ul class="package__description" v-html="packageData.description"/>

          <!-- Package variants -->
          <div class="package__variants" v-if="packageData.variants">
            <h4>Select one:</h4>
            <div class="package-variants__variant" v-for="variant in packageData.variants" :key="variant.id">
              <div class="formulate-input" data-classification="box">
                <div class="formulate-input-wrapper">
                  <div data-type="radio" class="formulate-input-element formulate-input-element--radio">
                    <input name="variant" type="radio" :id="variant.id" :value="variant.price" @change="selectVariant">
                    <label :for="variant.id" class="formulate-input-element-decorator white"/>
                  </div>
                  <label class="formulate-input-label formulate-input-label--after" :for="variant.id" v-html="variant.name"/>
                </div>
              </div>
            </div>
          </div>
          <br/>
          <!-- Total price -->
          <h4>{{ totalPriceFormatted }} USD</h4>
        </div>
        <div v-if="simpleSignup">
          <h2>Simple signup enabled</h2>
          <p>This account will not have a user timeline or payment details</p>
        </div>
      </div>
      <div class="signup-form col2">
        <h1>Register to get started</h1>
        <!-- Form -->
        <FormulateForm
          ref="signupForm"
          v-model="formValues"
          :schema="simpleSignup ? signupFormSchemaSimple : signupFormSchema"
          @submit="submitHandler"
        />

        <!-- "Make Admin" area -->
        <div v-if="simpleSignup">
          <label><input type="checkbox" v-model="makeAdminChecked"/> Make Admin</label>
          <p><input type="text" v-model="userAdminSignupPW" v-show="makeAdminChecked" placeholder="Master password"/></p>
        </div>

        <!-- Payment box -->
        <div class="payment-box" v-if="!simpleSignup">
          <h5>Enter Your Payment Details</h5>
          <p class="note">
            <strong>Note</strong>: your card will be securely held on file until we conduct a conflict check and you sign our engagement letter. Your card <strong>will not</strong> be charged if you do not sign the engagement letter to become a client of the firm.
          </p>

          <!-- Stripe Card Element -->
          <div id="card-element"></div>

          <!-- Validation errors -->
        </div>
        <div class="firebase-errors" v-if="fireBaseAuthError">
          Error: {{ fireBaseAuthError.message }}
        </div>

        <!-- Sign up button -->
        <button
          @click="signUpClick"
          :class="['button', 'button-signup', { loading: inProgress }]"
          :disabled="!signupEnabled && !simpleSignup && !devMode"
          v-html="inProgress ? 'Please Wait' : 'Sign Up'"
          />

        <div class="extras">
          <a href="/login">Back to Log In</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import { mapActions, mapMutations, mapState } from 'vuex'
import * as fb from '@/library/firebase'
import moment from 'moment'
import PasswordReset from '@/components/PasswordReset'
import signupFormSchema from '@/forms/signupFormSchema'
import signupFormSchemaSimple from '@/forms/signupFormSchemaSimple'
import SiteHeader from '../components/SiteHeader.vue'

export default {
  name: 'Signup',
  components: {
    PasswordReset,
    SiteHeader
  },
  data() {
    return {
      devMode: false,
      signupFormSchema,
      signupFormSchemaSimple,
      adminSignupPW: null,
      cardElement: null,
      formValues: {},
      inProgress: false,
      makeAdminChecked: false,
      userAdminSignupPW: '',
      package: null,
      packageData: null,
      paymentMethodId: null,
      selectedVariantPrice: 0,
      showPasswordReset: false,
      signupEnabled: false,
      simpleSignup: false,
      stripe: null,
      stripeSetupIntentClientSecret: '',
      stripeErrorMsg: '',
    }
  },
  computed: {
    ...mapState([
      'fireBaseAuthError'
    ]),
    date() {
      return moment().subtract(335, 'days').format('MM/DD/YY')
    },
    totalPrice() {
      return parseFloat(this.packageData.price) + this.selectedVariantPrice
    },
    totalPriceFormatted() {
      if (typeof this.packageData.price === 'string') {
        return this.packageData.price;
      }
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      return formatter.format(this.totalPrice)
    }
  },
  beforeMount() {
    const parsedUrl = new URL(window.location.href)
    if (parsedUrl.searchParams.get('simple') !== null) {
      this.simpleSignup = true
    }
    if (parsedUrl.searchParams.get('dev') !== null) {
      this.devMode = true
    }
  },
  async mounted() {
    // Initialize Stripe
    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
    if (!this.simpleSignup && !this.devMode) {
      this.createAndMountFormElements()
    }

    // Get the URL search param for "package"
    const urlParams = new URLSearchParams(window.location.search)
    this.package = urlParams.get('package')
    if (!this.package) {
      this.package = 'enhanced' // default to enhanced
    }

    // Get the package data from firebase
    fb.packagesCollection.doc(this.package).get().then(doc => {
      this.packageData = doc.data()

      // Click the first variant to select by default
      this.$nextTick(() => {
        const firstVariant = document.querySelector('.package-variants label')
        if (firstVariant) {
          firstVariant.click()
        }
      })
    })

    // Get the admin PW data
    fb.settingsCollection.doc('admin').get().then(doc => {
      this.adminSignupPW = doc.data().signupPassword
    })
  },
  methods: {
    ...mapMutations([
      'setFireBaseAuthError'
    ]),
    ...mapActions([
      'fetchUserProfile'
    ]),
    async createAndMountFormElements() {
      const elements = this.stripe.elements()

      var style = {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      }

      // Create an instance of the card Element.
      this.cardElement = elements.create('card', {style: style})

      // Add an instance of the card Element into the `card-element` <div>.
      this.cardElement.mount('#card-element')

      // When card element changes, enable or disable the sign up button
      this.cardElement.on('change', event => {
        this.signupEnabled = false
        if (event.complete) {
          this.signupEnabled = true
        }
      })
    },
    async confirmSetupIntent(userProfile){
      return new Promise((resolve, reject) => {
        this.stripe.confirmCardSetup(
          userProfile.stripe.setupIntentClientSecret,
          {
            payment_method: {
              card: this.cardElement,
              billing_details: {
                name: userProfile.firstName + ' ' + userProfile.lastName,
                email: userProfile.email
              }
            },
          }
        ).then(result => {
          if (result.error) {
            this.stripeErrorMsg = result.error.message
            this.signupEnabled = true
            reject(result.error)
          } else {
            resolve(result.setupIntent)
          }
        })
      })
    },
    selectVariant(e) {
      this.selectedVariantPrice = parseFloat(e.target.value)
    },
    signUpClick() {
      this.signupEnabled = false
      this.inProgress = true
      this.setFireBaseAuthError(null)
      this.$refs.signupForm.formSubmitted()
      this.$refs.signupForm.hasValidationErrors().then(hasErrors => {
        if (hasErrors) {
          const data = this.$refs.signupForm.formulateValue
          // If the only 2 errors are from the certifications radio inputs, allow submission
          if (document.querySelectorAll('[data-has-errors]').length <= 2 && (data.certification1 === 'disclosed' || data.certification2 === 'sold')) {
            this.submitHandler(data)
          }
          else {
            this.signupEnabled = true
            this.inProgress = false
          }
        }
      })
    },
    async submitHandler(data) {
      const checkedVariant = document.querySelector('.package-variants input:checked')
      data.variantId = checkedVariant ? checkedVariant.id : ''
      data.package = this.package
      if (this.simpleSignup) {
        data.simpleSignup = true
      }

      if (this.devMode) {
        const r = Math.floor(Math.random() * 9999)
        data = {
          "state": "AL",
          "email": "a" + r + "@bobmail.info",
          "password": "password",
          "firstName": "A" + r,
          "lastName": "Gaut",
          "phone": "15038666935",
          "businessName": "Seego Design",
          "address1": r + " Foo",
          "address2": "",
          "city": "Kapaa",
          "zip": "96746",
          "password_confirm": "password",
          "certification1": "neverDisclosed",
          "certification2": "neverSold",
          "variantId": "",
          "package": "enhanced"
        }
      }

      if (this.makeAdminChecked) {
        if (this.userAdminSignupPW === this.adminSignupPW) {
          data.isAdmin = true
        }
        else {
          this.$notify({
            group: 'default',
            type: 'error',
            title: 'Error',
            text: 'Incorrect admin password'
          })
          this.inProgress = false
          return
        }
      }

      this.$store.dispatch('signup', data).then(async user => {
        fb.usersCollection.doc(user.uid).set(user)
        if (!this.devMode && !this.simpleSignup) {
          fb.funcs.httpsCallable('createStripeCustomer')(user).then(res => {
            this.confirmSetupIntent(res.data).then(async setupIntent => {
              await fb.usersCollection.doc(user.uid).update({
                'stripe.paymentMethodId': setupIntent.payment_method
              })
              this.$store.dispatch('fetchUserProfile', user)
            }).catch(err => {
              this.$notify({
                group: 'default',
                type: 'error',
                title: 'Error',
                text: err.message
              })

              // Delete the user since there was a signup error (the account was created already)
              fb.usersCollection.doc(user.uid).delete()
              this.inProgress = false
            });
          })
        }
        else {
          if (user.isAdmin) {
            await fb.funcs.httpsCallable('updateUser')({
              uid: user.uid,
              makeAdmin: true
            })
            // get a new auth token so custom claims are immediately available
            fb.auth.currentUser.getIdToken(true)
          }
          this.$store.dispatch('fetchUserProfile', user)
        }
      }).catch((err) => {
        this.$notify({
          duration: 10000,
          group: 'default',
          title: 'Error',
          text: err.message,
          type: 'error'
        })
      })

    },
  },
  watch: {
    fireBaseAuthError(err) {
      this.signupEnabled = Boolean(err)
      this.inProgress = !err
    },
  }
}
</script>
<style lang="scss" scoped>
.col1, .col2 {
  padding: 2rem 3rem;
  @media ($md-up) {
    padding: 3rem 5rem;
  }
}
// signup
#signup {
  @media ($sm-down) {
    height: 100vh;
    background: $primary;
  }

  .col1,
  .col2 {
    @media ($sm-up) {
      min-height: 100vh;
    }
    @media ($sm-down) {
      height: auto;
      display: flex;
      align-items: center;
      flex-direction: column;
    }
  }

  .col1 {
    color: $white;
    background: linear-gradient(45deg, #4687c5, #37a7c0);
    flex-basis: 30%;
    max-width: 500px;
    @media ($sm-up) {
      background: linear-gradient(0deg, #4687c5, #37a7c0);
    }

    .logo {
      a {
        @include logo;
        &::before {
          position: static;
          margin-bottom: 20px;
          max-width: 280px;
        }
      }
    }

    h2 {
      margin: 0 0 20px !important;
      font-size: 1.5rem;
    }

    p {
      max-width: 490px;
      margin-top: 2rem;
      line-height: 1.8;
    }

    a {
      color: $white;
      text-decoration: underline;
    }

    .package {
      max-width: 300px;
      &__description {
        padding: 0;
        margin: 20px 0;

        /deep/ li {
          padding: 0.25rem 0;
          list-style: none;
          text-indent: -1rem;
          font-size: 14px;
          line-height: 1.8;
          &::before {
            content: '\2713';
            padding-right: .5rem;
          }
        }
      }

      &__variants {
        margin: 20px 0;
        label {
          display: block;
          padding: .25rem 0;
        }
      }
    }
  }


  .col2 {
    background-color: #fff;
    flex-basis: 60%;
    flex-shrink: 0;

    @media ($md-up) {
      flex-basis: 50%;
    }

    h1 {
      margin: 15px 0 35px;
    }

    form {
      margin: 0 auto;
    }

    .extras {
      float: right;
      text-align: right;
      @media ($md-down) {
        margin-top: 20px;
      }

      a {
        display: block;
        margin-bottom: 0.5rem;
      }
    }

    .password-reset {
      h1 {
        margin-bottom: 1rem;
      }

      p {
        margin-bottom: 2rem;
      }
    }
  }
}
// Form styles
@include formulateStyles;


// Stripe styles
.payment-box {
  margin: 20px auto;
  padding: 20px;
  border: solid 1px $c-border;
  background: top 10px right 14px no-repeat url(~@/assets/img/logo-stripe.png);
  background-size: 130px;

  h5 {
    margin: 0 0 1rem;
    font-size: 1.2rem;
    max-width: calc(100% - 110px);

  }

  #card-element {
    margin: 1rem 0 0;
    border: solid 1px $c-border;
    padding: 1rem;
  }

  .note {
    font-size: .9rem;
    padding: 1rem;
    margin: 1rem 0 0;
    background-color: $success-light;
  }

  button {
    background-color: #eee;
    border: solid 1px $c-border;
    line-height: 2;
    padding: 0 .5rem;
    border-radius: .25rem;
  }
}
// Firebase error message
.firebase-errors {
  padding: 20px;
  border: solid 1px $error;
  margin-bottom: 20px;
  color: $error;
}
</style>
